import { pickDefaultFontWeight, STANDARD_FONT_WEIGHTS } from "./fontWeightLabels";

export interface GoogleFontDefinition {
  family: string;
  weights: number[];
}

export interface GoogleFontBinary {
  family: string;
  weight: number;
  weightRange: [number, number];
  style: string;
  unicodeRanges: Array<[number, number]>;
  bytes: ArrayBuffer;
}

export const BASIC_GOOGLE_FONTS: GoogleFontDefinition[] = [
  { family: "Inter", weights: [400, 500, 600, 700] },
  { family: "Roboto", weights: [400, 500, 700] },
  { family: "Open Sans", weights: [400, 600, 700] },
  { family: "Lato", weights: [400, 700] },
  { family: "Montserrat", weights: [400, 600, 700] },
  { family: "Poppins", weights: [400, 600, 700] },
  { family: "Source Sans 3", weights: [400, 600, 700] },
  { family: "Merriweather", weights: [400, 700] },
  { family: "Playfair Display", weights: [400, 700] },
  { family: "IBM Plex Sans", weights: [400, 500, 700] },
];

const loadedFamilyStylesheets = new Set<string>();
const loadingFamilyStylesheets = new Map<string, Promise<boolean>>();
const loadedFontBytesByWeight = new Map<string, Promise<GoogleFontBinary[]>>();
const loadedFontWeights = new Map<string, number[]>();
const loadingFontWeights = new Map<string, Promise<number[]>>();
let textLayoutCacheInvalidator: (() => void) | undefined;

export function registerTextLayoutCacheInvalidator(invalidator: () => void): void {
  textLayoutCacheInvalidator = invalidator;
}

function invalidateTextLayoutCacheAfterFontLoad(): void {
  textLayoutCacheInvalidator?.();
}

export function weightsForGoogleFont(family: string): number[] {
  return loadedFontWeights.get(family)
    ?? BASIC_GOOGLE_FONTS.find(font => font.family === family)?.weights
    ?? [400, 700];
}

export function loadGoogleFontWeights(family: string): Promise<number[]> {
  const cached = loadedFontWeights.get(family);
  if (cached) {
    return loadGoogleFontFamily({ family, weights: cached }).then(() => cached);
  }

  const pending = loadingFontWeights.get(family);
  if (pending) {
    return pending;
  }

  const promise = fetchGoogleFontWeights(family)
    .then(async weights => {
      loadedFontWeights.set(family, weights);
      loadingFontWeights.delete(family);
      await loadGoogleFontFamily({ family, weights });
      return weights;
    })
    .catch(async () => {
      const fallback = weightsForGoogleFont(family);
      loadedFontWeights.set(family, fallback);
      loadingFontWeights.delete(family);
      await loadGoogleFontFamily({ family, weights: fallback });
      return fallback;
    });

  loadingFontWeights.set(family, promise);
  return promise;
}

async function fetchGoogleFontWeights(family: string): Promise<number[]> {
  const response = await fetch(googleFontsCssUrl([{
    family,
    weights: [...STANDARD_FONT_WEIGHTS],
  }]));
  if (!response.ok) {
    throw new Error(`Failed to fetch Google Fonts CSS: ${response.status}`);
  }

  const faces = parseFontFaces(await response.text())
    .filter(face => face.family === family && face.style === "normal");

  const weights = new Set<number>();
  for (const face of faces) {
    for (const weight of weightsFromFontFace(face.weightRange)) {
      weights.add(weight);
    }
  }

  const sorted = [...weights].sort((left, right) => left - right);
  if (sorted.length === 0) {
    throw new Error(`No weights found for ${family}.`);
  }
  return sorted;
}

function weightsFromFontFace(weightRange: [number, number]): number[] {
  const [min, max] = weightRange;
  if (min === max) {
    return [min];
  }
  return STANDARD_FONT_WEIGHTS.filter(weight => weight >= min && weight <= max);
}

export async function loadGoogleFonts(fonts: GoogleFontDefinition[] = BASIC_GOOGLE_FONTS): Promise<void> {
  await Promise.all(fonts.map(font => loadGoogleFontFamily(font)));
}

export function googleFontsStylesheetLoaded(fonts: GoogleFontDefinition[]): boolean {
  return fonts.every(font => loadedFamilyStylesheets.has(fontKey([font])));
}

/** Sync check: family stylesheet has finished loading (CSS + document.fonts). */
export function isGoogleFontLoaded(family: string): boolean {
  const prefix = `${family}:`;
  for (const key of loadedFamilyStylesheets) {
    if (key.startsWith(prefix)) {
      return true;
    }
  }
  return false;
}

async function loadGoogleFontFamily(font: GoogleFontDefinition): Promise<void> {
  const key = fontKey([font]);
  if (loadedFamilyStylesheets.has(key)) {
    return;
  }

  const pending = loadingFamilyStylesheets.get(key);
  if (pending) {
    await pending;
    return;
  }

  const promise = injectGoogleFontStylesheet(font);
  loadingFamilyStylesheets.set(key, promise);
  const loaded = await promise;
  loadingFamilyStylesheets.delete(key);
  if (loaded) {
    loadedFamilyStylesheets.add(key);
  }
}

async function injectGoogleFontStylesheet(font: GoogleFontDefinition): Promise<boolean> {
  if (typeof document === "undefined") {
    return true;
  }

  const key = fontKey([font]);
  if (document.head.querySelector(`style[data-google-fonts="${cssEscape(key)}"]`)) {
    return true;
  }

  try {
    const response = await fetch(googleFontsCssUrl([font]));
    const css = await response.text();
    if (!response.ok || !css.includes("@font-face")) {
      console.warn(`Google Font unavailable: ${font.family}`);
      return false;
    }

    const style = document.createElement("style");
    style.setAttribute("data-google-fonts", key);
    style.textContent = css;
    document.head.appendChild(style);

    await Promise.all(font.weights.map(weight =>
      document.fonts.load(`${weight} 16px "${font.family}"`).catch(() => undefined),
    ));
    invalidateTextLayoutCacheAfterFontLoad();
    return true;
  } catch (error) {
    console.warn(`Google Font unavailable: ${font.family}`, error);
    return false;
  }
}

function cssEscape(value: string): string {
  return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

export async function fetchGoogleFontBytes(fonts: GoogleFontDefinition[] = BASIC_GOOGLE_FONTS): Promise<GoogleFontBinary[]> {
  const results = await Promise.allSettled(
    fonts.flatMap(font => [...new Set(font.weights)].map(weight => fetchGoogleFontBytesForWeight(font.family, weight))),
  );
  return results.flatMap(result => result.status === "fulfilled" ? result.value : []);
}

export function warmGoogleFontBytes(family: string, weight: number): Promise<GoogleFontBinary[]> {
  return fetchGoogleFontBytesForWeight(family, weight);
}

export function warmGoogleFontDefinitions(fonts: GoogleFontDefinition[]): Promise<GoogleFontBinary[]> {
  return fetchGoogleFontBytes(fonts);
}

export async function googleFontBytesForText(family: string, weight: number, text: string): Promise<GoogleFontBinary | undefined> {
  const fonts = await warmGoogleFontBytes(family, weight);
  return bestFontMatch(fonts, family, weight, text);
}

async function fetchGoogleFontBytesForWeight(family: string, weight: number): Promise<GoogleFontBinary[]> {
  const key = `${family}:${weight}`;
  const existing = loadedFontBytesByWeight.get(key);
  if (existing) {
    return existing;
  }

  const promise = fetchGoogleFontBytesForWeightUncached(family, weight);
  loadedFontBytesByWeight.set(key, promise);
  return promise;
}

async function fetchGoogleFontBytesForWeightUncached(family: string, weight: number): Promise<GoogleFontBinary[]> {
  try {
    const response = await fetch(googleFontsCssUrl([{ family, weights: [weight] }]));
    if (!response.ok) {
      return [];
    }

    const css = await response.text();
    if (!css.includes("@font-face")) {
      return [];
    }

    const faces = parseFontFaces(css);
    const results = await Promise.allSettled(
      faces.map(async face => {
        const fontResponse = await fetch(face.url);
        if (!fontResponse.ok) {
          throw new Error(`Failed to fetch font file: ${fontResponse.status}`);
        }
        return {
          family: face.family,
          weight: face.weightRange[0] <= weight && face.weightRange[1] >= weight ? weight : face.weight,
          weightRange: face.weightRange,
          style: face.style,
          unicodeRanges: face.unicodeRanges,
          bytes: await fontResponse.arrayBuffer(),
        };
      }),
    );
    return results.flatMap(result => result.status === "fulfilled" ? [result.value] : []);
  } catch {
    return [];
  }
}

function bestFontMatch(fonts: GoogleFontBinary[], family: string, weight: number, text: string): GoogleFontBinary | undefined {
  const exactFamily = fonts.filter(font => font.family === family);
  const familyFonts = exactFamily.length > 0 ? exactFamily : fonts;
  const styleFonts = familyFonts.filter(font => font.style === "normal");
  const candidates = styleFonts.length > 0 ? styleFonts : familyFonts;
  if (candidates.length === 0) {
    return undefined;
  }

  return candidates.reduce((best, font) => fontMatchScore(font, weight, text) > fontMatchScore(best, weight, text) ? font : best, candidates[0]!);
}

function fontMatchScore(font: GoogleFontBinary, weight: number, text: string): number {
  const codePoints = textCodePoints(text);
  const covered = codePoints.filter(codePoint => fontCoversCodePoint(font, codePoint)).length;
  const coverageScore = codePoints.length === 0 ? 1 : covered / codePoints.length;
  return coverageScore * 10000 - fontWeightDistance(font, weight);
}

function fontWeightDistance(font: GoogleFontBinary, weight: number): number {
  const [min, max] = font.weightRange;
  if (weight >= min && weight <= max) {
    return 0;
  }
  return Math.min(Math.abs(weight - min), Math.abs(weight - max), Math.abs(weight - font.weight));
}

function fontCoversCodePoint(font: GoogleFontBinary, codePoint: number): boolean {
  return font.unicodeRanges.length === 0 || font.unicodeRanges.some(([start, end]) => codePoint >= start && codePoint <= end);
}

function textCodePoints(text: string): number[] {
  return [...new Set([...text].flatMap(character => {
    if (!/\S/.test(character)) {
      return [];
    }
    const codePoint = character.codePointAt(0);
    return codePoint === undefined ? [] : [codePoint];
  }))].sort((a, b) => a - b);
}

function parseFontFaces(css: string): Array<{ family: string; weight: number; weightRange: [number, number]; style: string; unicodeRanges: Array<[number, number]>; url: string }> {
  return [...css.matchAll(/@font-face\s*\{([^}]+)\}/g)].flatMap(match => {
    const block = match[1]!;
    const family = /font-family:\s*'([^']+)'/.exec(block)?.[1];
    const weightRange = parseFontWeightRange(/font-weight:\s*([^;]+)/.exec(block)?.[1]);
    const weight = weightRange[0] <= 400 && weightRange[1] >= 400 ? 400 : weightRange[0];
    const style = /font-style:\s*([^;]+)/.exec(block)?.[1]?.trim() ?? "normal";
    const unicodeRanges = parseUnicodeRanges(/unicode-range:\s*([^;]+)/.exec(block)?.[1]);
    const url = /url\((https:\/\/fonts\.gstatic\.com\/[^)]+)\)/.exec(block)?.[1];
    return family && url ? [{ family, weight, weightRange, style, unicodeRanges, url }] : [];
  });
}

function parseFontWeightRange(value: string | undefined): [number, number] {
  const weights = value?.match(/\d+/g)?.map(Number) ?? [];
  if (weights.length === 0) {
    return [400, 400];
  }
  if (weights.length === 1) {
    return [weights[0]!, weights[0]!];
  }
  return [Math.min(...weights), Math.max(...weights)];
}

function parseUnicodeRanges(value: string | undefined): Array<[number, number]> {
  if (!value) {
    return [];
  }

  return value.split(",").flatMap(part => {
    const match = /U\+([0-9A-F?]+)(?:-([0-9A-F]+))?/i.exec(part.trim());
    if (!match) {
      return [];
    }

    const rawStart = match[1]!;
    const rawEnd = match[2];
    if (rawStart.includes("?")) {
      return [[
        Number.parseInt(rawStart.replace(/\?/g, "0"), 16),
        Number.parseInt(rawStart.replace(/\?/g, "F"), 16),
      ]];
    }

    const start = Number.parseInt(rawStart, 16);
    const end = rawEnd ? Number.parseInt(rawEnd, 16) : start;
    return Number.isFinite(start) && Number.isFinite(end) ? [[start, end]] : [];
  });
}

function googleFontsCssUrl(fonts: GoogleFontDefinition[]): string {
  const families = fonts.map(font => {
    const weights = [...new Set(font.weights)].sort((a, b) => a - b).join(";");
    return `family=${encodeURIComponent(font.family)}:wght@${weights}`;
  });
  return `https://fonts.googleapis.com/css2?${families.join("&")}&display=swap`;
}

function fontKey(fonts: GoogleFontDefinition[]): string {
  return fonts.map(font => `${font.family}:${font.weights.join(",")}`).join("|");
}
